import React, { useEffect, useRef, useState } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { merge } from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts';
import { commaFormat } from '../new-report/utils/formatter';
import { DispType } from '../home/PortfolioStuffing/PortfolioStuffing';
import { rangeSelectorButtonAlignLeftChartClassName } from '../home/assetStuffing/AssetStuffingChart';
import { arrangeDateForChart } from '../utils/util';
import { marker } from '../utils/chartUtils';

export type ChartDatum = {
  x: Date;
  y: number;
};

export const dataToSeries = (
  data: ChartDatum[],
  dispType: DispType,
  isTwoWeeks: boolean
): Highcharts.SeriesOptionsType[] => {
  return [
    {
      type: 'line',
      color: dispType === '資産' ? '#43B5F7' : '#F743A4',
      name: '評価損益',
      data: data.map(datum => {
        return {
          x: arrangeDateForChart(datum.x).getTime(),
          y: datum.y,
        };
      }),
      marker: {
        enabled: isTwoWeeks,
        symbol: 'circle',
        radius: 4,
        lineWidth: 3,
        lineColor: dispType === '資産' ? '#43B5F7' : '#F743A4',
        fillColor: '#ffffff',
      },
    },
  ];
};

export const AssetsChart = ({
  data,
  dispType,
}: {
  data: ChartDatum[];
  dispType: DispType;
}) => {
  const [isTwoWeeks, setIsTwoWeeks] = useState<boolean>(true);
  const series = dataToSeries(data, dispType, isTwoWeeks);
  const initialOptions = getInitialOptions(dispType);
  const [options, setOptions] = useState<Highcharts.Options>(
    merge({
      ...initialOptions,
      series,
      rangeSelector: {
        enabled: true,
        dropdown: 'always',
        x: 30,
        buttonTheme: {
          fill: 'none',
          stroke: 'none',
          'stroke-width': 0,
          r: 6,
          style: {
            textAlign: 'center',
            color: '#8e9db2',
          },
          states: {
            hover: {
              fill: '#DEE5EE',
              style: {
                color: '#64748B',
                fontWeight: 'normal',
              },
            },
            select: {
              fill: '#DEE5EE',
              style: {
                color: '#64748B',
                fontWeight: 'normal',
              },
            },
          },
        },
        inputEnabled: false,
        buttons: [
          {
            type: 'week',
            count: 2,
            text: '14日間',
            title: '14日間',
          },
          {
            type: 'month',
            count: 1,
            text: '1ヶ月',
            title: '1ヶ月',
          },
          {
            type: 'month',
            count: 3,
            text: '3ヶ月',
            title: '3ヶ月',
          },
          {
            type: 'month',
            count: 6,
            text: '6ヶ月',
            title: '6ヶ月',
          },
          {
            type: 'year',
            count: 1,
            text: '1年',
            title: '1年',
          },
          {
            type: 'all',
            text: '全期間',
            title: '全期間',
          },
        ],
        selected: 0,
        buttonPosition: { align: 'left' },
      },
      xAxis: {
        ordinal: false,
        type: 'datetime',
        gridLineWidth: 0,
        dateTimeLabelFormats: {
          year: '%Y',
          month: '%Y/%m',
          day: '%m/%d',
          week: '%m/%d',
          hour: '%m/%d',
          minute: '%m/%d',
          second: '%m/%d',
          millisecond: '%m/%d',
        },
        events: {
          afterSetExtremes: function(event) {
            setIsTwoWeeks((event.max - event.min) / 1000 / 60 / 60 / 24 <= 14);
          },
        },
        labels: { style: { color: '#64748B' } },
        tickColor: '#CBD5E1',
        lineColor: '#CBD5E1',
      },
    })
  );

  useEffect(() => {
    setOptions(prev => {
      return merge({
        ...prev,
        series,
        rangeSelector: {
          buttonTheme: {
            selected: isTwoWeeks ? 0 : undefined,
          },
        },
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isTwoWeeks]);

  const ref = useRef<HighchartsReact.RefObject>(null);
  return (
    <>
      <HighchartsReact highcharts={Highcharts} options={options} ref={ref} />
    </>
  );
};

export const DebtChart = ({
  data,
  dispType,
}: {
  data: ChartDatum[];
  dispType: DispType;
}) => {
  const length = data.length;
  const defaultSelected =
    length >= 180
      ? 3
      : length < 180 && length >= 90
      ? 2
      : length < 90 && length >= 30
      ? 1
      : length < 30 && length >= 14
      ? 0
      : undefined;

  const [isTwoWeeks, setIsTwoWeeks] = useState<boolean>(true);
  const series = dataToSeries(data, dispType, isTwoWeeks);
  const initialOptions = getInitialOptions(dispType);
  const [options, setOptions] = useState<Highcharts.Options>(
    merge({
      ...initialOptions,
      series,
      rangeSelector: {
        enabled: true,
        dropdown: 'always',
        x: 30,
        buttonTheme: {
          fill: 'none',
          stroke: 'none',
          'stroke-width': 0,
          r: 6,
          style: {
            textAlign: 'center',
            color: '#8e9db2',
          },
          states: {
            hover: {
              fill: '#DEE5EE',
              style: {
                color: '#64748B',
                fontWeight: 'normal',
              },
            },
            select: {
              fill: '#DEE5EE',
              style: {
                color: '#64748B',
                fontWeight: 'normal',
              },
            },
          },
        },
        inputEnabled: false,
        buttons: [
          {
            type: 'week',
            count: 2,
            text: '14日間',
            title: '14日間',
          },
          {
            type: 'month',
            count: 1,
            text: '1ヶ月',
            title: '1ヶ月',
          },
          {
            type: 'month',
            count: 3,
            text: '3ヶ月',
            title: '3ヶ月',
          },
          {
            type: 'month',
            count: 6,
            text: '6ヶ月',
            title: '6ヶ月',
          },
          {
            type: 'year',
            count: 1,
            text: '1年',
            title: '1年',
          },
          {
            type: 'all',
            text: '全期間',
            title: '全期間',
          },
        ],
        selected: defaultSelected,
        buttonPosition: { align: 'left' },
      },
      xAxis: {
        ordinal: false,
        type: 'datetime',
        gridLineWidth: 0,
        dateTimeLabelFormats: {
          year: '%Y',
          month: '%Y/%m',
          day: '%m/%d',
          week: '%m/%d',
          hour: '%m/%d',
          minute: '%m/%d',
          second: '%m/%d',
          millisecond: '%m/%d',
        },
        events: {
          afterSetExtremes: function(event) {
            setIsTwoWeeks((event.max - event.min) / 1000 / 60 / 60 / 24 <= 14);
          },
        },

        labels: { style: { color: '#64748B' } },
        tickColor: '#CBD5E1',
        lineColor: '#CBD5E1',
      },
    })
  );

  useEffect(() => {
    setOptions(prev => {
      return merge({
        ...prev,
        series,
        rangeSelector: {
          buttonTheme: {
            selected: isTwoWeeks ? 0 : undefined,
          },
        },
        yAxis: {
          reversed: true,
        },
      });
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isTwoWeeks]);

  const ref = useRef<HighchartsReact.RefObject>(null);
  return (
    <>
      <HighchartsReact highcharts={Highcharts} options={options} ref={ref} />
    </>
  );
};

const getInitialOptions = (dispType: DispType) => {
  return {
    exporting: { enabled: false },
    title: { text: '' },
    legend: {
      enabled: false,
    },
    credits: {
      enabled: false,
    },
    navigator: {
      enabled: false,
    },
    tooltip: {
      borderColor: 'transparent',
      useHTML: true,
      formatter: function(): string {
        const Tooltip = ({ date }: { date: Date }): React.ReactElement => {
          return (
            <>
              <div
                style={{
                  display: 'grid',
                  gap: '2px',
                  gridAutoRows: 'min-content',
                  gridAutoFlow: 'row',
                }}>
                <div style={{ paddingBottom: '3px', fontSize: '9px' }}>
                  {date.getFullYear()}/{date.getMonth() + 1}/{date.getDate()}
                </div>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'min-content min-content min-content',
                    placeItems: 'center start',
                    gap: '2px',
                  }}>
                  <div
                    style={{
                      borderRadius: '50%',
                      height: '5px',
                      width: '5px',
                      gridColumn: 1,
                      backgroundColor: this.color.toString(),
                    }}
                  />
                  <span style={{ fontSize: '10px', gridColumn: 2 }}>
                    {dispType === '資産' ? '資産総額' : '負債金額'}:
                  </span>
                  <span
                    style={{
                      fontWeight: 'bold',
                      fontSize: '10px',
                      gridColumn: 3,
                    }}>
                    {commaFormat(this.y / 10000, { numberOfDecimals: 2 })}万円
                  </span>
                </div>
              </div>
            </>
          );
        };
        return renderToStaticMarkup(<Tooltip date={new Date(this.x)} />);
      },
    },
    yAxis: {
      labels: {
        formatter: function(e): string {
          return typeof this.value === 'number'
            ? commaFormat(this.value / 10000, { numberOfDecimals: 2 })
            : '';
        },
        align: 'left',
        x: 3,
        y: 4,
        style: { color: '#64748B' },
      },
      title: {
        text: '(万円)',
        align: 'high',
        offset: 0,
        rotation: 0,
        y: -10,
        style: { color: '#64748B' },
      },
      gridLineColor: '#CBD5E1',
      opposite: true,
      alignTicks: false,
    },
    chart: {
      className: rangeSelectorButtonAlignLeftChartClassName,
      animation: {
        duration: 0,
      },
      height: 210,
      marginBottom: 20,
    },
    plotOptions: {
      series: { ...marker, turboThreshold: 15000 },
      area: {
        events: {
          legendItemClick: (): false => false,
        },
        turboThreshold: 15000,
      },
      line: {
        events: {
          legendItemClick: (): false => false,
        },
        turboThreshold: 15000,
      },
    },
  } as Highcharts.Options;
};
